import { authHeader } from "@roman/shared/lib/auth/authHeader";
import romanApi, { type RomanApiError } from "@roman/shared/lib/net/romanApi";
import type { ChildPredictionMarketComment, ParentPredictionMarketComment } from "@roman/shared/types/Comment";
import useSWRMutation, { type SWRMutationConfiguration } from "swr/mutation";

type CreateParentCommentInput = {
  type: "parent";
  predictionMarketId: string;
  text: string;
  mentionedUserIds: string[];
};

type CreateChildCommentInput = {
  type: "child";
  predictionMarketId: string;
  text: string;
  mentionedUserIds: string[];
  parentId: string;
};

type CreateCommentInput = CreateParentCommentInput | CreateChildCommentInput;

type CreateCommentResponse = {
  comment: ParentPredictionMarketComment | ChildPredictionMarketComment;
};

const fetcher = async (_url: string, { arg }: { arg: CreateCommentInput }): Promise<CreateCommentResponse> => {
  const headers = authHeader.getOrThrow();

  if (arg.type === "child") {
    // Use the replies endpoint for child comments
    const { type, parentId, predictionMarketId, ...body } = arg;
    return romanApi.post<
      CreateCommentResponse,
      Omit<CreateChildCommentInput, "type" | "parentId" | "predictionMarketId">
    >(`/prediction-market/${predictionMarketId}/comments/${parentId}/replies`, body, { headers });
  }

  // Use the main comments endpoint for parent comments
  const { type, predictionMarketId, ...body } = arg;
  return romanApi.post<CreateCommentResponse, Omit<CreateParentCommentInput, "type" | "predictionMarketId">>(
    `/prediction-market/${predictionMarketId}/comments`,
    body,
    { headers },
  );
};

export const usePostComment = (
  swrOptions?: SWRMutationConfiguration<CreateCommentResponse, RomanApiError, string, CreateCommentInput>,
  onSuccess?: (
    comment: Omit<ParentPredictionMarketComment, "mentionedUsers"> | Omit<ChildPredictionMarketComment, "parentId">,
  ) => void,
) => {
  const mergedOptions = {
    ...swrOptions,
    onSuccess: (
      data: CreateCommentResponse,
      key: string,
      config: Readonly<SWRMutationConfiguration<CreateCommentResponse, RomanApiError, string, CreateCommentInput, any>>,
    ) => {
      if (swrOptions && typeof swrOptions.onSuccess === "function") {
        swrOptions.onSuccess(data, key, config);
      }
      if (onSuccess) onSuccess(data.comment);
    },
  };

  const { trigger, error, isMutating } = useSWRMutation<
    CreateCommentResponse,
    RomanApiError,
    string,
    CreateCommentInput
  >("post-comment", fetcher, mergedOptions);

  return {
    postComment: trigger,
    isMutating,
    error,
  };
};
